import { MOBILE_MEDIA_QUERY } from '@evisort/eds/constants/breakpoints';
import {
  SIDE_NAV_COLLAPSED_WIDTH,
  SIDE_NAV_WIDTH,
} from '@evisort/eds/constants/sideNav';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useUserPreferences } from '~/redux/api/rolodex/useUserPreferences';

import { useSidebarNavPreference } from './SidebarNavPreferenceContext';
import { useIsSidebarNavAvailable } from './useIsSidebarNavAvailable';

interface SidebarNavContextValue {
  isCollapsed: boolean;
  isMobile: boolean;
  toggleCollapsed: () => void;
  isSidebarLayoutActive: boolean;
  isSidebarLayoutResolving: boolean;
  width: number;
}

const SidebarNavContext = createContext<SidebarNavContextValue>({
  isCollapsed: false,
  isMobile: false,
  toggleCollapsed: () => {},
  isSidebarLayoutActive: false,
  isSidebarLayoutResolving: false,
  width: 0,
});

function matchesMobile() {
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

export function SidebarNavProvider({ children }: PropsWithChildren) {
  const { sidebarNavCollapsed, setSidebarNavCollapsed } = useUserPreferences();
  // Mobile forces the drawer shut without touching the persisted preference,
  // so reopening it there doesn't overwrite the desktop choice.
  const [isMobileCollapsed, setIsMobileCollapsed] =
    useState<boolean>(matchesMobile);

  // Initialise synchronously so the first render already knows the viewport,
  // preventing a flash of the desktop sidebar on narrow screens.
  const [isMobile, setIsMobile] = useState<boolean>(matchesMobile);

  useEffect(() => {
    const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

    function handleChange(event: MediaQueryListEvent) {
      setIsMobile(event.matches);
      // Entering mobile: hide the sidebar so content fills the screen.
      if (event.matches) setIsMobileCollapsed(true);
    }

    mobileQuery.addEventListener('change', handleChange);
    return () => mobileQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleCollapsed = useCallback(() => {
    if (isMobile) {
      setIsMobileCollapsed((prev) => !prev);
      return;
    }
    setSidebarNavCollapsed((prev) => !prev);
  }, [isMobile, setSidebarNavCollapsed]);

  const isSidebarNavAvailable = useIsSidebarNavAvailable();
  const { isOptedIn, isOptedInLoading } = useSidebarNavPreference();
  const isSidebarLayoutActive = isSidebarNavAvailable && isOptedIn;
  const isSidebarLayoutResolving = isSidebarNavAvailable && isOptedInLoading;
  const isCollapsed = isMobile ? isMobileCollapsed : sidebarNavCollapsed;
  let width = 0;
  if (isSidebarLayoutActive && !isMobile) {
    width = isCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;
  }

  const value = useMemo(
    () => ({
      isCollapsed,
      isMobile,
      toggleCollapsed,
      isSidebarLayoutActive,
      isSidebarLayoutResolving,
      width,
    }),
    [
      isCollapsed,
      isMobile,
      toggleCollapsed,
      isSidebarLayoutActive,
      isSidebarLayoutResolving,
      width,
    ],
  );

  return (
    <SidebarNavContext.Provider value={value}>
      {children}
    </SidebarNavContext.Provider>
  );
}

export function useSidebarNav() {
  return useContext(SidebarNavContext);
}
